import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { type CICaseDetail } from 'components/CriticalIncident/interfaces';
import { useScreenSize } from 'components/Layout';
import { Spinner } from 'components/Shared';
import { Button } from 'components/Shared/Inputs';
import { CICaseSummaryModal } from './CICaseSummaryModal/CICaseSummaryModal';
import { Overview } from './CICaseSummaryTabs/Overview';
import { IncidentReportTab } from './CICaseSummaryTabs/IncidentReportTab';
import { TimesheetTab } from './CICaseSummaryTabs/TimesheetTab';
import { InterventionSummary } from './CICaseSummaryTabs/IncidentReportTab/forms/InterventionSummary';
import { IncidentDocumentation } from './CICaseSummaryTabs/IncidentReportTab/forms/IncidentDocumentation';
import { IndividualSupportSession } from './CICaseSummaryTabs/IncidentReportTab/forms/IndividualSupportSession';
import { GroupServices } from './CICaseSummaryTabs/IncidentReportTab/forms/GroupServices';
import { OnsiteContact } from './CICaseSummaryTabs/IncidentReportTab/forms/OnsiteContact';
import { FollowUps } from './CICaseSummaryTabs/IncidentReportTab/forms/FollowUps';
import { Recommendations } from './CICaseSummaryTabs/IncidentReportTab/forms/Recommendations';
import { RiskAssessments } from './CICaseSummaryTabs/IncidentReportTab/forms/RiskAssessments';
import { Review } from './CICaseSummaryTabs/IncidentReportTab/forms/Review';
import { DeliveryHours } from './CICaseSummaryTabs/IncidentReportTab/forms/DeliveryHours';
import { ReferringManager } from './CICaseSummaryTabs/IncidentReportTab/forms/ReferringManager';
import { useFeatureManager } from 'hooks/useFeatureManager';

import './CICaseSummary.scss';

export const CICaseSummary: FC<{ ciCase: CICaseDetail }> = ({ ciCase }) => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
    const location = useLocation();
    const { isMobile } = useScreenSize();
    const { isIncidentReportFeatureEnabled } = useFeatureManager();
    const [ modalOpened, setModalOpened ] = useState<boolean>(false);

    const handleClose = () => {
      setModalOpened(false);
    }

    const basePath = `critical_incident/cases/${ciCase.caseId}/activities/${ciCase.activityId}`;

    function getCurrentTabValue() {
        const match =
            location.pathname.replace(/^(\/)/, '').replace(/\/critical_incident\/cases\/\d+\/activities\/\d+\//, '') ||
            basePath;
        // keep incident report tab highlighted even when a child route is selected
        const finalMatch =   match.includes('report/') ? `${basePath}/report` : match;

        return `/${finalMatch}`;
    }

    return translationsReady ? (
        <div className="case_summary_content_wrapper">
            {isMobile && (
                <>
                    <div className="mobile_case_summary_header">
                        <h1>{[ciCase.organization?.company, ciCase.organization?.branch, ciCase.organization?.division].filter(Boolean).join(', ')}</h1>
                        <Button
                            variant="text"
                            size="small"
                            className="summary_button"
                            onClick={() => setModalOpened(true)}
                        >
                            {t('summary')}
                        </Button>
                    </div>
                    <CICaseSummaryModal open={modalOpened} handleClose={handleClose} ciCase={ciCase} />
                </>
            )}
            <Tabs
                className="case_summary_tabs"
                indicatorColor="primary"
                variant="scrollable"
                scrollButtons={false}
                value={getCurrentTabValue()}
            >
                <Tab
                    className="button_tab"
                    component={Link}
                    value={`/${basePath}`}
                    label={t('overview', 'Overview')}
                    to={`/${basePath}`}
                />
                {isIncidentReportFeatureEnabled() && (
                    <Tab
                        className="button_tab"
                        component={Link}
                        value={`/${basePath}/report`}
                        label={t('incident_report')}
                        to={`/${basePath}/report`}
                    />
                )}
                {isIncidentReportFeatureEnabled() && (
                    <Tab
                        className="button_tab"
                        component={Link}
                        value={`/${basePath}/timesheet`}
                        label={t('timesheet_and_expenses')}
                        to={`/${basePath}/timesheet`}
                    />
                )}
            </Tabs>
            <Routes>
                <Route path="/" element={<Overview ciCase={ciCase!} />} />
                {isIncidentReportFeatureEnabled() && (<>
                    <Route path="/report" element={<IncidentReportTab />}>
                        <Route index element={<InterventionSummary />} />
                        <Route path="intervention-summary" element={<InterventionSummary />} />
                        <Route path="incident-documentation" element={<IncidentDocumentation />} />
                        <Route path="individual-support-session" element={<IndividualSupportSession />} />
                        <Route path="group-service" element={<GroupServices />} />
                        <Route path="onsite-contact" element={<OnsiteContact />} />
                        <Route path="follow-ups" element={<FollowUps />} />
                        <Route path="recommendations" element={<Recommendations />} />
                        <Route path="risk-assessments" element={<RiskAssessments />} />
                        <Route path="review" element={<Review />} />
                        <Route path="delivery-hours" element={<DeliveryHours />} />
                        <Route path="referring-manager" element={<ReferringManager />} />
                    </Route>
                    <Route path="/timesheet" element={<TimesheetTab />} />
                </>)}
            </Routes>
        </div>
    ) : (
        <Spinner fillParent />
    );
};